import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Category as CategoryIcon,
  Label as LabelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import categoryService from '../services/categoryService';
import DashboardLayout from '../components/DashboardLayout';

const CategoryManagement = () => {
  // State for categories
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // State for dialogs
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openSubcategoryDialog, setOpenSubcategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [selectedCategoryForSub, setSelectedCategoryForSub] = useState(null);

  // State for forms
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    icon: '',
    color: '#3B82F6',
    isActive: true,
    sortOrder: 0
  });

  const [subcategoryForm, setSubcategoryForm] = useState({
    name: '',
    description: '',
    isActive: true,
    sortOrder: 0
  });

  // State for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm
      };
      const data = await categoryService.getAllCategories(params);
      setCategories(data.categories);
      setTotalCount(data.total);
    } catch (error) {
      showSnackbar('Failed to fetch categories', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [page, rowsPerPage, searchTerm]);

  // Utility functions
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      description: '',
      icon: '',
      color: '#3B82F6',
      isActive: true,
      sortOrder: 0
    });
    setEditingCategory(null);
  };

  const resetSubcategoryForm = () => {
    setSubcategoryForm({
      name: '',
      description: '',
      isActive: true,
      sortOrder: 0
    });
    setEditingSubcategory(null);
    setSelectedCategoryForSub(null);
  };

  // Category handlers
  const handleCreateCategory = () => {
    resetCategoryForm();
    setOpenCategoryDialog(true);
  };

  const handleEditCategory = (category) => {
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      icon: category.icon || '',
      color: category.color || '#3B82F6',
      isActive: category.isActive,
      sortOrder: category.sortOrder || 0
    });
    setEditingCategory(category);
    setOpenCategoryDialog(true);
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await categoryService.updateCategory(editingCategory._id, categoryForm);
        showSnackbar('Category updated successfully');
      } else {
        await categoryService.createCategory(categoryForm);
        showSnackbar('Category created successfully');
      }
      setOpenCategoryDialog(false);
      resetCategoryForm();
      fetchCategories();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to save category', 'error');
    }
  };

  const handleDeleteCategory = async (category) => {
    if (window.confirm(`Are you sure you want to delete "${category.name}"? This will also delete all subcategories.`)) {
      try {
        await categoryService.deleteCategory(category._id);
        showSnackbar('Category deleted successfully');
        fetchCategories();
      } catch (error) {
        showSnackbar(error.response?.data?.message || 'Failed to delete category', 'error');
      }
    }
  };

  // Subcategory handlers
  const handleCreateSubcategory = (category) => {
    resetSubcategoryForm();
    setSelectedCategoryForSub(category);
    setOpenSubcategoryDialog(true);
  };

  const handleEditSubcategory = (category, subcategory) => {
    setSubcategoryForm({
      name: subcategory.name,
      description: subcategory.description || '',
      isActive: subcategory.isActive,
      sortOrder: subcategory.sortOrder || 0
    });
    setEditingSubcategory(subcategory);
    setSelectedCategoryForSub(category);
    setOpenSubcategoryDialog(true);
  };

  const handleSaveSubcategory = async () => {
    try {
      if (editingSubcategory) {
        await categoryService.updateSubcategory(
          selectedCategoryForSub._id,
          editingSubcategory._id,
          subcategoryForm
        );
        showSnackbar('Subcategory updated successfully');
      } else {
        await categoryService.addSubcategory(selectedCategoryForSub._id, subcategoryForm);
        showSnackbar('Subcategory created successfully');
      }
      setOpenSubcategoryDialog(false);
      resetSubcategoryForm();
      fetchCategories();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to save subcategory', 'error');
    }
  };

  const handleDeleteSubcategory = async (category, subcategory) => {
    if (window.confirm(`Are you sure you want to delete "${subcategory.name}"?`)) {
      try {
        await categoryService.deleteSubcategory(category._id, subcategory._id);
        showSnackbar('Subcategory deleted successfully');
        fetchCategories();
      } catch (error) {
        showSnackbar(error.response?.data?.message || 'Failed to delete subcategory', 'error');
      }
    }
  };

  const categoryActions = (
    <Button
      variant="contained"
      startIcon={<AddIcon />}
      onClick={handleCreateCategory}
    >
      Add Category
    </Button>
  );

  return (
    <DashboardLayout title="Category Management" actions={categoryActions} maxWidth="lg">
      <Box sx={{ maxWidth: 'lg', mx: 'auto', px: 3, py: 2 }}>

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Categories List */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {categories.map((category) => (
          <Accordion key={category._id}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel-${category._id}-content`}
              id={`panel-${category._id}-header`}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <CategoryIcon sx={{ mr: 2, color: category.color }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6">{category.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {category.description || 'No description'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                  <Chip
                    label={category.isActive ? 'Active' : 'Inactive'}
                    color={category.isActive ? 'success' : 'default'}
                    size="small"
                  />
                  <Chip
                    label={`${category.subcategoryCount} subcategories`}
                    variant="outlined"
                    size="small"
                  />
                  <Tooltip title="Edit Category">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditCategory(category);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Category">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCategory(category);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Subcategories</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleCreateSubcategory(category)}
                  >
                    Add Subcategory
                  </Button>
                </Box>
                
                {category.subcategories && category.subcategories.length > 0 ? (
                  <Grid container spacing={2}>
                    {category.subcategories.map((subcategory) => (
                      <Grid item xs={12} sm={6} md={4} key={subcategory._id}>
                        <Card variant="outlined">
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <LabelIcon sx={{ mr: 1, fontSize: 16 }} />
                              <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                                {subcategory.name}
                              </Typography>
                              <Chip
                                icon={subcategory.isActive ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                label={subcategory.isActive ? 'Active' : 'Inactive'}
                                color={subcategory.isActive ? 'success' : 'default'}
                                size="small"
                              />
                            </Box>
                            {subcategory.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {subcategory.description}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                              <Tooltip title="Edit Subcategory">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditSubcategory(category, subcategory)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Subcategory">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteSubcategory(category, subcategory)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No subcategories found. Click "Add Subcategory" to create one.
                  </Typography>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Category Dialog */}
      <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Category' : 'Add New Category'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Category Name"
              fullWidth
              variant="outlined"
              value={categoryForm.name}
              onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={categoryForm.description}
              onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Icon (Material-UI icon name)"
              fullWidth
              variant="outlined"
              value={categoryForm.icon}
              onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Color"
              type="color"
              variant="outlined"
              value={categoryForm.color}
              onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
              sx={{ mb: 2, width: 120 }}
            />
            <TextField
              margin="dense"
              label="Sort Order"
              type="number"
              variant="outlined"
              value={categoryForm.sortOrder}
              onChange={(e) => setCategoryForm({ ...categoryForm, sortOrder: parseInt(e.target.value) || 0 })}
              sx={{ mb: 2, width: 120, ml: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={categoryForm.isActive}
                  onChange={(e) => setCategoryForm({ ...categoryForm, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenCategoryDialog(false)}
            variant="contained"
            data-cancel="true"
          >
            Cancel
          </Button>
          <Button onClick={handleSaveCategory} variant="contained">
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subcategory Dialog */}
      <Dialog open={openSubcategoryDialog} onClose={() => setOpenSubcategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSubcategory ? 'Edit Subcategory' : 'Add New Subcategory'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Category: {selectedCategoryForSub?.name}
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Subcategory Name"
              fullWidth
              variant="outlined"
              value={subcategoryForm.name}
              onChange={(e) => setSubcategoryForm({ ...subcategoryForm, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={subcategoryForm.description}
              onChange={(e) => setSubcategoryForm({ ...subcategoryForm, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Sort Order"
              type="number"
              variant="outlined"
              value={subcategoryForm.sortOrder}
              onChange={(e) => setSubcategoryForm({ ...subcategoryForm, sortOrder: parseInt(e.target.value) || 0 })}
              sx={{ mb: 2, width: 120 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={subcategoryForm.isActive}
                  onChange={(e) => setSubcategoryForm({ ...subcategoryForm, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenSubcategoryDialog(false)}
            variant="contained"
            data-cancel="true"
          >
            Cancel
          </Button>
          <Button onClick={handleSaveSubcategory} variant="contained">
            {editingSubcategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </DashboardLayout>
  );
};

export default CategoryManagement; 